import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UserService } from 'netsocial-posting/src/app/services/user/user.service';
import { ToastController, ModalController, Platform, AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AccountService } from 'netsocial-posting/src/app/services/account/account.service';
import { OnboardingCarouselComponent } from '../components/onboarding/onboarding-carousel/onboarding-carousel.component';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import {
  ResourceGroup,
  ResourceManagementComponent,
} from '../components/account/resource-management/resource-management.component';
import { ApiRequestService } from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import { MainNavigationComponent } from '../components/app-shell/main-navigation/main-navigation.component';
import { TourIntroComponent } from '../components/onboarding/tour-intro/tour-intro.component';
import { hasSubString } from 'netsocial-lib/src/app/utils/string-utils';
import { StorageService } from 'netsocial-lib/src/app/services/storage/storage.service';
import { computed } from 'mobx';
import { Deploy } from 'cordova-plugin-ionic/dist/ngx';
import { versions } from 'src/environments/versions';

@Component({
  selector: 'app-workspace',
  templateUrl: './workspace.page.html',
  styleUrls: ['./workspace.page.scss'],
})
export class WorkspacePage implements OnInit, OnDestroy {
  currentStep: number;

  @ViewChild(MainNavigationComponent, { static: false })
  mainNavigation: MainNavigationComponent;

  private onboardingModalRef: HTMLIonModalElement;

  private queryParamSubscription: Subscription;
  private tourStepSubscription: Subscription;

  private userSubscription?: Subscription;

  private startModalTimeout = false;

  @computed
  get isDesktop(): boolean {
    return this.platform.is('desktop');
  }

  constructor(
    public readonly userService: UserService,
    public readonly accountService: AccountService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastController: ToastController,
    private readonly storage: StorageService,
    private readonly modal: ModalController,
    private readonly apiRequest: ApiRequestService,
    private readonly errorService: ErrorService,
    private readonly platform: Platform,
    private readonly deploy: Deploy,
    private readonly alert: AlertController
  ) {}

  ngOnInit(): void {
    this.queryParamSubscription = this.route.queryParams.subscribe(
      ({ step, extensionLogin, showResources, cid }) => {
        this.currentStep = step === undefined ? 0 : Number(step);

        if (!this.userSubscription) {
          this.userSubscription = this.userService.userSubject.asObservable().subscribe(() => {
            this.startOnboarding();
          });
        }

        if (this.onboardingModalRef) {
          this.onboardingModalRef.componentProps?.stepObservable.next(this.currentStep);
        }

        if (extensionLogin) {
          this.userService.sendLoginMessage();
          this.router.navigate(['workspace'], { queryParams: { step } });
        }

        if (showResources && cid) {
          this.showResources(step, cid);
        }
      }
    );

    this.cookieCheck();

    this.tourStepSubscription = this.userService.tourStepBehaviorSubject
      .asObservable()
      .subscribe((step) => {
        if (step && hasSubString(step.name, 'nav:')) {
          this.mainNavigation.menu.open();
        } else if (step) {
          this.mainNavigation.menu.close();
        }
      });

    if (typeof relicxSDK !== 'undefined') this.relicxSessionVars();
  }

  ngOnDestroy(): void {
    this.queryParamSubscription?.unsubscribe();
    this.tourStepSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
  }

  relicxSessionVars(): void {
    try {
      relicxSDK.sessionVars({
        userId: this.userService.currentUser?.id,
        email: this.userService.currentUser?.email,
        fullName: this.userService.currentUser?.fullName,
        clientName: this.userService.currentUser?.clientInfo?.name,
        timezone: this.userService.currentUser?.timezone,
        timezoneOffset: this.userService.currentUser?.timezoneOffset,
        emailValid: !!this.userService.currentUser?.emailValidNum,
        isOnboarded: !!this.userService.currentUser?.isOnboardedNum,
        roleLabel: this.userService.currentUser?.roleLabel,
        planName: this.userService.currentUser?.permissionInfo.planName,
        revision: versions.revision,
      });
    } catch (error) {
      console.log(error);
    }
  }

  relicxIdentify(): void {
    try {
      relicxSDK?.identify(this.userService.currentUser?.id, {
        email: this.userService.currentUser?.email,
        fullName: this.userService.currentUser?.fullName,
        clientName: this.userService.currentUser?.clientInfo?.name,
        timezone: this.userService.currentUser?.timezone,
        timezoneOffset: this.userService.currentUser?.timezoneOffset,
        emailValid: !!this.userService.currentUser?.emailValidNum,
        isOnboarded: !!this.userService.currentUser?.isOnboardedNum,
        roleLabel: this.userService.currentUser?.roleLabel,
        planName: this.userService.currentUser?.permissionInfo.planName,
        revision: versions.revision,
      });
    } catch (error) {
      console.log(error);
    }
  }

  async cookieCheck(): Promise<void> {
    const title = await firstValueFrom(this.translate.get('cookie.title'));
    const cookieMessage = await firstValueFrom(this.translate.get('cookie.message'));
    const cookiesAccepted = await this.storage.get('cookiesAccepted');
    const understoodText = await firstValueFrom(this.translate.get('understoodText'));

    if (!cookiesAccepted) {
      const toast = await this.toastController.create({
        message: `<div>${title}</div><div>${cookieMessage}</div>`,
        cssClass: 'cookie-notification',
        buttons: [
          {
            side: 'end',
            icon: 'checkmark',
            text: `${understoodText}`,
            handler: () => {
              this.storage.set('cookiesAccepted', true)?.then(() => {
                toast.dismiss();
              });
            },
          },
        ],
      });

      toast.present();
    }
  }

  private async showResources(step: string, cid: string): Promise<void> {
    const accounts = await this.errorService.resolve(
      this.accountService.getAccounts({
        connectionId: cid,
      }),
      {
        errorMessage: 'workspace.errors.getNewConnectionAccount',
      }
    );

    let loader: HTMLIonLoadingElement;
    if (accounts?.items.length && accounts.items[0].hasResources) {
      const modal = await this.modal.create({
        component: ResourceManagementComponent,
        componentProps: {
          account: accounts.items[0],
          hideUntilLoaded: true,
          ionSave: () => {
            this.apiRequest.contextOwner.next(this.apiRequest.contextOwner.value);
            modal.dismiss();
          },
          ionGroupResourcesLoaded: async (groups: ResourceGroup[]) => {
            loader.dismiss();
            // if there are no resources available to be connected then
            // close the window and inform the user
            if (!groups.find((g) => g.type === 'not-connected')?.resources.length) {
              this.errorService.toastMessage('noResourcesMessage');
              await modal.dismiss();
            }
          },
        },
      });

      const header = await firstValueFrom(this.translate.get('workspace.reconnected.header'));
      const subHeader = await firstValueFrom(this.translate.get('workspace.reconnected.subHeader'));
      const alert = await this.alert.create({
        header,
        subHeader,
        buttons: [
          { text: 'No', role: 'cancel' },
          {
            text: 'Yes',
            handler: async () => {
              loader = await this.errorService.showLoader();
              await modal.present();
            },
          },
        ],
      });

      await alert.present();
      await modal.onDidDismiss();
      await this.router.navigate([location.pathname], {
        queryParams: { step },
      });
    }
  }

  private async showTourIntro(): Promise<void> {
    const modal = await this.modal.create({
      component: TourIntroComponent,
      cssClass: 'tour-intro-dialog',
    });

    return modal.present();
  }

  private async startOnboarding(): Promise<void> {
    if (
      !this.userService.currentUser?.isOnboardedNum &&
      !this.startModalTimeout &&
      !this.onboardingModalRef
    ) {
      this.startModalTimeout = true;
      setTimeout(
        async () => {
          const modal = await this.modal.create({
            component: OnboardingCarouselComponent,
            backdropDismiss: false,
            keyboardClose: false,
            componentProps: {
              stepObservable: new BehaviorSubject<number>(this.currentStep),
              currentStep: this.currentStep,
              ionChangeStep: (step: number) => {
                this.changeStep(step === 0 ? undefined : step);
              },
            },
            cssClass: 'onboarding-dialog',
          });

          this.onboardingModalRef = modal;

          await modal.present();

          modal.onWillDismiss().then(() => {
            this.changeStep(undefined);
            if (this.isDesktop) {
              this.showTourIntro();
            }
          });
        },
        this.currentStep === 0 ? 1500 : 0
      );
    }
  }

  private async changeStep(step?: number): Promise<void> {
    await this.router.navigate(['workspace'], { queryParams: { step } });
  }
}
